import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./styles/WirelessInfrastructure.css";

const wirelessTabs = [
  {
    key: "coverage",
    label: "Coverage",
    title: "Wireless coverage designed for indoor, outdoor, and hotspot environments",
    text: "ComNetworks helps plan and implement wireless coverage across WiFi hotspots, outdoor broadband wireless, fixed wireless access, and mobile wireless access environments.",
    items: [
      "WiFi hotspots",
      "Outdoor broadband wireless / WiFi",
      "Fixed wireless access",
      "Mobile wireless access",
    ],
  },
  {
    key: "transport",
    label: "Transport",
    title: "Build wireless links between sites, zones, and distributed endpoints",
    text: "Wireless transport options support connectivity between buildings, remote areas, surveillance points, and multi site environments through point to point and point to multi point architectures.",
    items: [
      "Point to point wireless",
      "Point to multi point wireless",
      "Wireless backhaul support",
      "Multi location connectivity planning",
    ],
  },
  {
    key: "services",
    label: "Voice & Media",
    title: "Support multimedia and voice traffic across wireless environments",
    text: "Wireless infrastructure can be shaped for multimedia traffic, VoIP communication, and service quality needs where consistent performance matters across users and devices.",
    items: [
      "Wireless / WiFi for multimedia",
      "VoIP support",
      "Traffic aware deployment planning",
      "Stronger service experience across endpoints",
    ],
  },
  {
    key: "mesh",
    label: "Mesh & Surveillance",
    title: "Use wireless mesh and surveillance to extend reach and visibility",
    text: "Multi service mesh networks and wireless video surveillance help support broader wireless coverage, distributed camera deployments, and flexible expansion across hard-to-wire spaces.",
    items: [
      "Multi service mesh networks",
      "Wireless video surveillance",
      "Coverage extension across larger environments",
      "Support for difficult or distributed layouts",
    ],
  },
];

const topologyNodes = [
  {
    title: "WiFi Hotspots",
    text: "Support guest, public, or local access zones with managed wireless coverage.",
  },
  {
    title: "Outdoor Broadband",
    text: "Extend access beyond indoor spaces with outdoor wireless reach and distribution.",
  },
  {
    title: "VoIP & Multimedia",
    text: "Support richer traffic types across voice, media, and user heavy environments.",
  },
  {
    title: "Wireless Surveillance",
    text: "Connect video surveillance points where wired deployment is difficult or costly.",
  },
];

const transportCards = [
  {
    title: "Point-to-Point",
    text: "Direct wireless links between two locations for site connectivity, backhaul, or remote extension.",
  },
  {
    title: "Point-to-Multi-Point",
    text: "Distribute connectivity from one hub to multiple endpoints across wider service areas.",
  },
  {
    title: "Mesh Networks",
    text: "Use multiple nodes to expand coverage and create more flexible wireless pathways across an environment.",
  },
  {
    title: "Fixed & Mobile Access",
    text: "Support different access patterns depending on whether users are stationary, distributed, or moving.",
  },
];

const fitCards = [
  {
    title: "Sites needing broad wireless coverage",
    text: "For organizations planning WiFi coverage across offices, campuses, venues, or open outdoor areas.",
  },
  {
    title: "Distributed and hard to wire environments",
    text: "For sites where mesh, point-to-point, or point-to-multi-point links are more practical than traditional cabling.",
  },
  {
    title: "Voice, media, and high usage wireless needs",
    text: "For environments that need wireless support for multimedia, VoIP, and active user traffic.",
  },
  {
    title: "Surveillance and visibility projects",
    text: "For businesses deploying wireless video surveillance across larger or more complex physical layouts.",
  },
];

export default function WirelessInfrastructure() {
  const [activeKey, setActiveKey] = useState("coverage");
  const activeTab =
    wirelessTabs.find((tab) => tab.key === activeKey) || wirelessTabs[0];

  return (
    <div className="wirelessPage">
      <section className="wirelessHero">
        <div className="wirelessContainer wirelessHero__container">
          <div className="wirelessHero__content">
            <p className="wirelessEyebrow">Wireless Infrastructure</p>

            <h1 className="wirelessHero__title">
              Plan wireless environments around coverage, reach, mobility, and media demand.
            </h1>

            <p className="wirelessHero__text">
              ComNetworks offers a comprehensive range of services to plan and
              implement wireless networks across hotspots, outdoor broadband,
              multimedia, VoIP, mesh networks, point-to-point links,
              point-to-multi-point links, fixed and mobile access, and wireless
              video surveillance.
            </p>

            <div className="wirelessHero__actions">
              <Link to="/contact" className="wirelessBtn wirelessBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="wirelessBtn wirelessBtn--secondary">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="wirelessHero__visual">
            <div className="signalPanel">
              <div className="signalPanel__top">
                <span className="signalBadge">Planned wireless</span>
                <span className="signalBadge signalBadge--soft">Coverage + transport</span>
              </div>

              <div className="signalBoard">
                <div className="signalHub">Wireless Core</div>
                <div className="signalNode signalNode--one">Hotspots</div>
                <div className="signalNode signalNode--two">Mesh</div>
                <div className="signalNode signalNode--three">VoIP</div>
                <div className="signalNode signalNode--four">Surveillance</div>

                <span className="signalWave signalWave--one" />
                <span className="signalWave signalWave--two" />
                <span className="signalLink signalLink--one" />
                <span className="signalLink signalLink--two" />
                <span className="signalLink signalLink--three" />
                <span className="signalLink signalLink--four" />
              </div>

              <div className="signalPanel__bottom">
                <div className="signalMetric">
                  <strong>Multi mode wireless</strong>
                  <span>Coverage, transport, mobility, and surveillance working together</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wirelessTabsSection">
        <div className="wirelessContainer">
          <div className="wirelessSectionHead wirelessSectionHead--center">
            <p className="wirelessEyebrow">Wireless views</p>
            <h2 className="wirelessTitle">
              A different tab style for this page: floating pills inside a signal shell
            </h2>
            <p className="wirelessText">
              This interaction style is intentionally different from the previous
              pages so the Wireless Infrastructure page feels new and more movement oriented.
            </p>
          </div>

          <div className="wirelessTabsShell">
            <div className="wirelessTabsNav">
              {wirelessTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`wirelessTabPill ${activeKey === tab.key ? "active" : ""}`}
                  onClick={() => setActiveKey(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="wirelessTabsPanel">
              <div className="wirelessTabsPanel__intro">
                <p className="wirelessTabsLabel">{activeTab.label}</p>
                <h3>{activeTab.title}</h3>
                <p>{activeTab.text}</p>
              </div>

              <div className="wirelessTabsPanel__grid">
                {activeTab.items.map((item) => (
                  <div key={item} className="wirelessMiniBox">
                    <span className="wirelessMiniBox__dot" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wirelessTopologySection">
        <div className="wirelessContainer">
          <div className="wirelessSectionHead">
            <p className="wirelessEyebrow">Service topology</p>
            <h2 className="wirelessTitle">
              Unique section style: diagonal topology cards instead of a standard grid
            </h2>
            <p className="wirelessText">
              Wireless services often connect different use cases across the same
              environment, so this section uses angled positioning to suggest movement and spread.
            </p>
          </div>

          <div className="wirelessTopologyGrid">
            {topologyNodes.map((item, index) => (
              <article
                key={item.title}
                className={`wirelessTopologyCard ${
                  index % 2 !== 0 ? "wirelessTopologyCard--raised" : ""
                }`}
              >
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="wirelessTransportSection">
        <div className="wirelessContainer">
          <div className="wirelessTransportBand">
            <div className="wirelessTransportBand__intro">
              <p className="wirelessEyebrow">Transport models</p>
              <h2 className="wirelessTitle">
                Point-to-point, point-to-multi-point, mesh, and access models in one transport view
              </h2>
              <p className="wirelessText">
                Instead of repeating regular cards, this section uses larger framed
                transport boxes to make the layout feel different from the past service pages.
              </p>
            </div>

            <div className="wirelessTransportGrid">
              {transportCards.map((item) => (
                <article key={item.title} className="wirelessTransportCard">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="wirelessCoverageSection">
        <div className="wirelessContainer">
          <div className="wirelessCoveragePanel">
            <div className="wirelessCoveragePanel__content">
              <p className="wirelessEyebrow">Coverage and deployment</p>
              <h2 className="wirelessTitle">
                Wireless planning is about both access experience and deployment practicality
              </h2>
              <p className="wirelessText">
                Wireless networks are often chosen not only for mobility, but also
                for faster deployment, wider reach, and more flexibility where cabling is difficult.
              </p>
            </div>

            <div className="wirelessCoverageStats">
              <div className="wirelessCoverageStat">
                <strong>Hotspots</strong>
                <span>Public and managed access zones</span>
              </div>
              <div className="wirelessCoverageStat">
                <strong>Mesh</strong>
                <span>Coverage extension through distributed nodes</span>
              </div>
              <div className="wirelessCoverageStat">
                <strong>Access</strong>
                <span>Fixed and mobile wireless service options</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wirelessFitSection">
        <div className="wirelessContainer">
          <div className="wirelessSectionHead wirelessSectionHead--center">
            <p className="wirelessEyebrow">Best fit</p>
            <h2 className="wirelessTitle">
              Where Wireless Infrastructure services create the most value
            </h2>
            <p className="wirelessText">
              These services work especially well where broad coverage, wireless
              transport, media traffic, and flexible deployment all matter.
            </p>
          </div>

          <div className="wirelessFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="wirelessFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="wirelessCta">
        <div className="wirelessContainer wirelessCta__container">
          <div className="wirelessCta__content">
            <p className="wirelessEyebrow wirelessEyebrow--light">Next step</p>
            <h2 className="wirelessCta__title">
              Build a wireless network around the way your environment actually operates
            </h2>
            <p className="wirelessCta__text">
              If you need help with hotspots, outdoor broadband, mesh, wireless
              access, VoIP, multimedia, or wireless surveillance, ComNetworks can
              help plan the right wireless infrastructure model.
            </p>
          </div>

          <div className="wirelessCta__actions">
            <Link to="/contact" className="wirelessBtn wirelessBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="wirelessBtn wirelessBtn--ghost">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
